import {useEffect, useRef, useState} from 'react';
import {animate, useMotionValue, type MotionValue} from 'framer-motion';

const thresholds = [.025, .32, .58, .82];
const capabilities = [0, 4, 6, 8];
const ease = [.4, 0, .2, 1] as const;

/** Scroll selects a destination; each transition finishes on its own clock. */
export function useJourneyPlayback(progress: MotionValue<number>, enabled: boolean) {
  const dock = useMotionValue(0);
  const [active, setActive] = useState(0);
  const [chapter, setChapter] = useState(0);
  const [manual, setManual] = useState(false);
  const activeRef = useRef(0);
  const manualRef = useRef(false);

  const choose = (index: number) => {
    manualRef.current = true;
    activeRef.current = index;
    setManual(true);
    setActive(index);
  };

  useEffect(() => {
    if (!enabled) {
      dock.set(0);
      return;
    }

    let current = 0, target = 0, playing = false, disposed = false;
    let animation: ReturnType<typeof animate<number>> | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tween = async (value: MotionValue<number>, to: number, duration: number) => {
      animation = animate(value, to, {type: 'tween', duration, ease});
      await animation;
    };

    const play = async () => {
      if (playing || disposed) return;
      playing = true;
      while (!disposed && current !== target) {
        // New input changes only target. Never cancel the transition in flight.
        const next = target;
        if ((current > 0) !== (next > 0)) {
          await tween(dock, next > 0 ? 1 : 0, .85);
          if (disposed) return;
        }
        setChapter(Math.max(0, next - 1));
        const index = capabilities[Math.max(0, next - 1)];
        if (!manualRef.current && index !== activeRef.current) {
          activeRef.current = index;
          setActive(index);
          // Keep the interface visible while its local 320 ms transitions finish.
          await new Promise<void>(resolve => { timer = setTimeout(resolve, 320); });
          if (disposed) return;
        }
        current = next;
      }
      playing = false;
    };

    const select = (position: number) => {
      // Separate forward/backward thresholds avoid flickering at a boundary.
      while (target < thresholds.length && position >= thresholds[target]) target++;
      while (target > 0 && position < thresholds[target - 1] - .02) target--;
      void play();
    };
    select(progress.get());
    const unsubscribe = progress.on('change', select);
    return () => {
      disposed = true;
      unsubscribe();
      animation?.stop();
      clearTimeout(timer);
    };
  }, [progress, enabled, dock]);

  return {dock, active, chapter, manual, choose};
}
